/**
 * LayoutPickerCell — table-cell editor for a document type's form layout
 * assignment, for the inline-editable document-types table (sibling of
 * `NumberingCell` — same anchor-button + `CellPopover` pattern).
 *
 * Layout lists can be long, so (like `AssignLayoutModal`, which this mirrors)
 * a plain native `<select>` isn't enough — the popover hosts a search box +
 * scrollable option list (name + „(domyślny)" suffix + „systemowy" origin
 * badge for centrally-sourced layouts).
 *
 * Async error-recovery mirrors `NumberingCell`/`useInlineCellEdit`: `onCommit`
 * may return a Promise — while pending the popover stays open and disabled;
 * on reject it STAYS OPEN with the rejection message inline; on resolve it
 * closes. Returning void closes immediately (sync fire-and-forget).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CellPopover } from '@/components/additional/CellPopover';
import { OriginBadge } from '@/components/documents/OriginBadge';
import { Input } from '@/components/Input';
import type { FormLayoutOption } from '@/components/settings/DocumentTypeFormModal';
import { normalizeSearch } from '@/lib/search-normalize';

export interface LayoutPickerCellProps {
  layouts: FormLayoutOption[];
  /** Aktualnie przypisany layout — `null` = kaskada domyślna kartoteki. */
  value: string | null;
  /**
   * Wywoływane po wyborze opcji. Może zwrócić Promise: reject zostawia popover
   * otwarty + pokazuje komunikat, resolve zamyka popover. Zwrot void zamyka od razu.
   */
  onCommit: (layoutId: string | null) => void | Promise<void>;
  disabled?: boolean;
  'data-testid'?: string;
}

/** Narrow an unknown onCommit return to a thenable (async commit path). */
function isPromiseLike<T>(x: unknown): x is PromiseLike<T> {
  return (
    typeof x === 'object' && x !== null && typeof (x as { then?: unknown }).then === 'function'
  );
}

export function LayoutPickerCell({
  layouts,
  value,
  onCommit,
  disabled,
  'data-testid': testId,
}: LayoutPickerCellProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const committingRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const current = layouts.find((l) => l.id === value) ?? null;

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setSearch('');
    setError(null);
    setOpen(true);
  }, [disabled]);

  // Anuluj / Escape / click-outside (CellPopover onClose). Frozen while an async
  // commit is pending — its result would otherwise land on a closed popover.
  const handleClose = useCallback(() => {
    if (committingRef.current) return;
    setOpen(false);
    setError(null);
  }, []);

  const normalizedSearch = search ? normalizeSearch(search) : '';
  const filtered = useMemo(
    () =>
      normalizedSearch
        ? layouts.filter((l) => normalizeSearch(l.name).includes(normalizedSearch))
        : layouts,
    [layouts, normalizedSearch],
  );

  const commit = useCallback(
    (nextId: string | null) => {
      if (committingRef.current) return;
      // No-op skip (spójnie z InlineEditableCell.isEqual): brak zmiany = tylko zamknij.
      if (nextId === value) {
        setOpen(false);
        return;
      }
      const result = onCommit(nextId);
      if (isPromiseLike<void>(result)) {
        committingRef.current = true;
        setCommitting(true);
        void Promise.resolve(result).then(
          () => {
            committingRef.current = false;
            if (!isMountedRef.current) return;
            setCommitting(false);
            setError(null);
            setOpen(false);
          },
          (reason: unknown) => {
            committingRef.current = false;
            if (!isMountedRef.current) return;
            setCommitting(false);
            setError(reason instanceof Error ? reason.message : String(reason));
          },
        );
      } else {
        setError(null);
        setOpen(false);
      }
    },
    [value, onCommit],
  );

  const rowClass = (isSelected: boolean) =>
    `flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary ${
      isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : ''
    }`;

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={testId}
        className={[
          'w-full truncate rounded px-2 py-1 text-left text-sm text-text-primary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50',
          open ? 'bg-bg-tertiary ring-2 ring-accent-500' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {current ? current.name : <span className="text-text-muted">—</span>}
      </button>
      <CellPopover
        open={open}
        anchorRef={anchorRef}
        onClose={handleClose}
        ariaLabel={t('tenant.documentTypes.assignLayout.layoutLabel')}
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <div className="w-80 space-y-2 p-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tenant.documentTypes.assignLayout.searchPlaceholder')}
            aria-label={t('tenant.documentTypes.assignLayout.searchPlaceholder')}
            disabled={committing}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <div
            role="listbox"
            aria-label={t('tenant.documentTypes.assignLayout.layoutLabel')}
            className="max-h-64 overflow-y-auto rounded-lg border border-border-primary"
          >
            {/* „— brak —" zawsze widoczne (czyści wskazanie), niezależne od filtra. */}
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              onClick={() => commit(null)}
              disabled={committing}
              className={`${rowClass(value === null)} border-b border-border-primary disabled:cursor-not-allowed disabled:opacity-50`}
              data-testid={testId ? `${testId}-option-none` : undefined}
            >
              <span className="italic text-text-secondary">
                {t('tenant.documentTypes.assignLayout.none')}
              </span>
            </button>

            {filtered.map((l) => {
              const isSelected = value === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => commit(l.id)}
                  disabled={committing}
                  className={`${rowClass(isSelected)} border-b border-border-primary last:border-b-0 disabled:cursor-not-allowed disabled:opacity-50`}
                  data-testid={testId ? `${testId}-option-${l.id}` : undefined}
                >
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    {l.isDefault
                      ? `${l.name} ${t('tenant.documentTypes.form.formLayoutDefaultSuffix')}`
                      : l.name}
                  </span>
                  {l.sourceViewSlug ? (
                    <OriginBadge
                      origin="global"
                      data-testid={testId ? `${testId}-origin-${l.id}` : undefined}
                    />
                  ) : null}
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div
                className="px-3 py-2 text-sm text-text-secondary"
                data-testid={testId ? `${testId}-empty` : undefined}
              >
                {t('tenant.documentTypes.assignLayout.noResults')}
              </div>
            )}
          </div>
          {error != null && (
            <p
              role="alert"
              data-testid={testId ? `${testId}-error` : undefined}
              className="text-xs text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
        </div>
      </CellPopover>
    </>
  );
}
